// Category-sidebar helpers shared across the assessment flows (Enterprise, TPRM, ...).
// Field names differ per flow (e.g. `tprmQuestionCategory` vs `questionCategory`), so
// callers pass the category/id field names for their question shape.

// Regroups questions so each category's questions are contiguous (question banks often
// interleave categories). Categories keep first-appearance order; questions keep their
// incoming order within a category.
export function groupQuestionsByCategory(questions, categoryField = 'category') {
    const categories = [];
    const byCategory = new Map();
    (questions || []).forEach(q => {
        const category = q[categoryField] || 'General';
        if (!byCategory.has(category)) {
            byCategory.set(category, []);
            categories.push(category);
        }
        byCategory.get(category).push(q);
    });
    return categories.flatMap(c => byCategory.get(c));
}

// Builds the { category, questions, answeredCount } list consumed by
// AssessmentSectionSidebar. `questions` should already be in display order.
export function buildAssessmentSections(questions, { categoryField = 'category', idField = 'id', textField = 'text', responses = {} } = {}) {
    const list = [];
    const indexByCategory = new Map();
    (questions || []).forEach(q => {
        const category = q[categoryField] || 'General';
        if (!indexByCategory.has(category)) {
            indexByCategory.set(category, list.length);
            list.push({ category, questions: [] });
        }
        list[indexByCategory.get(category)].questions.push(q);
    });
    return list.map(s => {
        const answered = s.questions.filter(q => responses[String(q[idField])]);
        return {
            ...s,
            answeredCount: answered.length,
            // Jump target when this section is picked from the sidebar: the last question the
            // user actually answered, so returning to a partially-filled category resumes near
            // where they left off instead of always restarting at question 1. Falls back to the
            // section's first question when nothing is answered yet — and also when EVERYTHING
            // is: on a fully-answered section "resume" would pin every click to the section's
            // last question (for the final section that reads "Question N of N" with Next
            // dead-ended, making the section look like it only has one question).
            jumpQuestion: answered.length > 0 && answered.length < s.questions.length
                ? answered[answered.length - 1]
                : s.questions[0],
            // Flat list for the "jump to question N" dropdown — a section can hold 20-30+
            // questions, and Next/Previous alone means clicking through all of them one at a
            // time to reach, say, question 20. Kept separate from `questions` (not mutated) so
            // nothing else relying on that array's shape is affected. `text` backs the hover
            // tooltip on each option so users can see which question a number refers to
            // before jumping to it.
            questionOptions: s.questions.map((q, i) => ({
                id: q[idField],
                position: i + 1,
                answered: Boolean(responses[String(q[idField])]),
                text: q[textField] || '',
            })),
        };
    });
}
